// Gruvetto score and event categories on screen (ADR 0009): categories are stored as letters, players see point ranges.
import { EVENT_CATS, catForScore, type CatId } from '@gruvetto/core';
import { esc } from '../dom.js';
import { t } from '../../i18n/index.js';

const known = (c: string): c is CatId => c in EVENT_CATS;
/** Score categories from the strongest to the weakest; E (open) is not a level. */
const LADDER: readonly CatId[] = ['A', 'B', 'C', 'D'];

/** Translated name of a category (its score range), the core text as a fallback. */
export const catName = (c: string): string => t.events.screen.cats[c]?.name ?? (known(c) ? EVENT_CATS[c].name : c);
export const catDesc = (c: string): string => t.events.screen.cats[c]?.desc ?? (known(c) ? EVENT_CATS[c].desc : '');
export const catColor = (c: string): string => (known(c) ? EVENT_CATS[c].color : '#888888');

/** Category pills: neutral, the category colour only on the dot. `mine` stands out. */
export const catBadges = (cats: string[], mine?: string | null): string =>
  cats.map(c => `<span class="cat-pill ${c === mine ? 'me' : ''}" style="--c:${catColor(c)}"><i></i>${esc(catName(c))}</span>`).join('');

/**
 * Compact summary for lists: one dot per level (lit when the event offers it) and, when the event has a race for
 * the player, the name of that category. Keeps a calendar row readable where four pills used to compete.
 */
export function catSummary(cats: string[], mine: CatId): string {
  const names = cats.map(catName).join(' · ');
  const dots = LADDER.map(c => `<i class="${cats.includes(c) ? 'on' : ''}${c === mine ? ' me' : ''}" style="--c:${catColor(c)}"></i>`).join('');
  const label = cats.includes(mine) ? catBadges([mine], mine) : cats.includes('E') ? catBadges(['E']) : '';
  return `<span class="cat-sum" title="${esc(names)}" aria-label="${esc(names)}"><span class="cat-dots">${dots}</span>${label}</span>`;
}

/** The player's score, with the dot of their natural category. */
export function scoreBadge(score: number): string {
  const c = EVENT_CATS[catForScore(score)].color;
  return `<span class="badge score" title="${esc(t.charts.scoreTitle)}" style="--c:${c}"><i></i>${esc(t.charts.score(Math.round(score)))}</span>`;
}
